import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { SOURCE_BY_ID, STATUS_BY_ID } from './sources';

const formatDate = (iso, locale = 'en-AE') => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'done' || status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

/**
 * Module 6 — Action log list row.
 *
 * Compact card showing source chip + title + due date + status pill.
 * Tap → calls onSelect(action). Used in both mobile list and desktop split-view list.
 *
 * Props:
 *  - action: action_log row
 *  - selected: highlight when this row is the active detail target (desktop)
 *  - onSelect: (action) => void
 */
const ActionLogRow = ({ action, selected = false, onSelect }) => {
  const { t } = useTranslation();
  if (!action) return null;
  const src = SOURCE_BY_ID[action.source_activity] ?? SOURCE_BY_ID.creator_alert;
  const sta = STATUS_BY_ID[action.status] ?? STATUS_BY_ID.open;
  const overdue = isOverdue(action.due_date, action.status);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(action)}
      style={{
        all: 'unset',
        display: 'block',
        width: '100%',
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
    >
      <Card
        padded
        style={{
          padding: 12,
          background: selected ? `${TCN.cyan}12` : TCN.surface1,
          border: selected ? `1px solid ${TCN.cyan}55` : `1px solid ${TCN.border}`,
          transition: `all ${TCN.durBase} ${TCN.ease}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: `${src.color}22`, color: src.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={src.icon} size={14} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 2,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, color: src.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t(`tcn.m6.source.${action.source_activity}`, src.label)}
              </span>
              <span style={{ fontSize: 10, color: TCN.textMute }}>·</span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: sta.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {t(`tcn.m6.status.${action.status}`, sta.label)}
              </span>
              {overdue && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: TCN.red,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginInlineStart: 'auto',
                  }}
                >
                  {t('tcn.m6.overdue', 'Overdue')}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: TCN.text,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {action.title}
            </div>
            <div
              style={{
                fontSize: 11,
                color: TCN.textDim,
                marginTop: 4,
                display: 'flex',
                gap: 10,
              }}
            >
              <span>
                {t('tcn.m6.due', 'Due')} {formatDate(action.due_date)}
              </span>
              {action.description && (
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  · {action.description}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>
    </button>
  );
};

export default ActionLogRow;
